import { useState, useEffect } from 'react';
import { FileText, Zap, Loader2, Settings, Info, Globe, Tag, Link, DollarSign, Eye, EyeOff } from 'lucide-react';
import { generateArticleInteractive, type PromptApprovalCallback, type OutlineApprovalCallback, type CostUpdateCallback } from '../../lib/content-generation/interactive-orchestrator';
import { useAuth } from '../../contexts/AuthContext';
import { GenerationProgress, ArticleOutline } from '../../lib/content-generation';
import { toast } from '../../lib/toast';
import { PromptEditor } from './PromptEditor';
import { OutlineEditor } from './OutlineEditor';
import { analyzeContent, ContentAnalysis } from '../../lib/content-intent-detector';
import { extractPageData, formatExtractedDataForPrompt, ExtractedPageData } from '../../lib/url-content-extractor';
import { setContentOverrides } from '../../lib/prompt-snippets';
import { GenerationCost } from '../../lib/openai-chat';

type GenerationMode = 'auto' | 'structure-control' | 'full-control';

interface ArticleGeneratorProps {
  analysisId: string;
  competitors: any[];
  keywords: {
    additional: string[];
    missing: string[];
    present: string[];
  };
  projectDescription: string;
  projectPrompt?: string; // Custom AI prompt from project settings
  selectedDomains?: string[]; // Selected competitors for content generation
  language: string;
  keyword: string;
  url?: string;
  serpResults?: any[];
  onComplete: (html: string) => void;
}

export function ArticleGenerator({
  analysisId,
  competitors,
  keywords,
  projectDescription,
  projectPrompt,
  selectedDomains = [],
  language,
  keyword,
  url,
  serpResults = [],
  onComplete
}: ArticleGeneratorProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<GenerationProgress | null>(null);
  const [generationMode, setGenerationMode] = useState<GenerationMode>('structure-control');
  const [humanizeContent, setHumanizeContent] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showCost, setShowCost] = useState(false);
  
  // Check if user is admin
  const { user } = useAuth();
  const isAdmin = user?.user_metadata?.role === 'admin' || user?.email === 'andmaillev@gmail.com';
  
  // Content analysis
  const [contentAnalysis, setContentAnalysis] = useState<ContentAnalysis | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedPageData | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Cost tracking
  const [totalCost, setTotalCost] = useState<GenerationCost | null>(null);
  const [currentStageCost, setCurrentStageCost] = useState<{ stage: string; cost: GenerationCost } | null>(null);
  
  // User overrides
  const [selectedLanguage, setSelectedLanguage] = useState<string>(language);
  const [selectedContentType, setSelectedContentType] = useState<string>('');
  const [selectedIntent, setSelectedIntent] = useState<string>('');
  
  // Prompt editor state
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [currentPromptData, setCurrentPromptData] = useState<{
    stageName: string;
    stageNumber: number;
    prompt: string;
    resolver: (value: string) => void;
  } | null>(null);

  // Outline editor state
  const [showOutlineEditor, setShowOutlineEditor] = useState(false);
  const [currentOutlineData, setCurrentOutlineData] = useState<{
    outline: ArticleOutline;
    resolver: (value: ArticleOutline) => void;
  } | null>(null);

  // Аналізуємо контент при завантаженні
  useEffect(() => {
    const analysis = analyzeContent({
      keyword,
      language,
      serpResults
    });
    setContentAnalysis(analysis);
    setSelectedContentType(analysis.contentType);
    setSelectedIntent(analysis.intent);
    console.log('📊 Content Analysis:', analysis);
  }, [keyword, language, serpResults]);
  
  // Синхронізуємо мову
  useEffect(() => {
    setSelectedLanguage(language);
  }, [language]);

  // Витягуємо дані з URL якщо він є
  useEffect(() => {
    if (url && url.startsWith('http')) {
      setIsAnalyzing(true);
      extractPageData(url)
        .then(data => {
          setExtractedData(data);
          console.log('🔍 Extracted URL data:', data);
        })
        .catch(err => {
          console.error('Failed to extract URL data:', err);
        })
        .finally(() => {
          setIsAnalyzing(false);
        });
    }
  }, [url]);

  const handleGenerate = async () => {
    if (isGenerating) return;

    // Validate we have required data
    if (!competitors || competitors.length === 0) {
      toast.error('No competitor data available. Please run analysis first.');
      return;
    }

    if (!keywords.additional || keywords.additional.length === 0) {
      toast.error('No SEO keywords available. Please add keywords first.');
      return;
    }

    // Встановлюємо overrides перед генерацією
    setContentOverrides({
      language: selectedLanguage,
      contentType: selectedContentType,
      intent: selectedIntent
    });

    setIsGenerating(true);
    setShowCost(false);
    setTotalCost(null);
    setCurrentStageCost(null);
    const loadingToast = toast.loading('Starting article generation...');

    try {
      // Prepare competitor data - filter only selected domains from content_analysis
      console.log('🔍 Selected domains for content generation:', selectedDomains);
      
      const competitorData = competitors
        .filter(comp => {
          const isSelected = selectedDomains.includes(comp.domain);
          console.log(`  Competitor ${comp.domain}: ${isSelected ? '✅ selected' : '❌ skipped'}`);
          return isSelected;
        })
        .map(comp => ({
          domain: comp.domain || 'unknown',
          url: comp.url || '',
          headers: {
            h1: comp.headers?.h1 || [],
            h2: comp.headers?.h2 || [],
            h3: comp.headers?.h3 || [],
            h4: comp.headers?.h4 || [],
            h5: comp.headers?.h5 || [],
            h6: comp.headers?.h6 || []
          },
          text: comp.text || comp.content || '',
          word_count: comp.word_count || comp.wordCount || 0
        }));
      
      console.log('📊 Filtered competitors for generation:', competitorData.length, 'out of', competitors.length);

      // Prompt approval callback - behavior depends on mode
      const onPromptRequest: PromptApprovalCallback = (stageName, stageNumber, defaultPrompt) => {
        return new Promise((resolve) => {
          // Auto mode: skip all prompts
          if (generationMode === 'auto') {
            resolve(defaultPrompt);
            return;
          }
          
          // Structure-control mode: only stage 1 (outline)
          // Stage 1 = outline prompt, Stage 2 = sections, Stage 3+ = assembly/polish
          if (generationMode === 'structure-control' && stageNumber !== 1) {
            resolve(defaultPrompt);
            return;
          }
          
          // Full-control mode or stage 1 for structure-control: show modal
          setCurrentPromptData({
            stageName,
            stageNumber,
            prompt: defaultPrompt,
            resolver: resolve
          });
          setShowPromptEditor(true);
        });
      };

      // Outline approval callback - behavior depends on mode
      const onOutlineRequest: OutlineApprovalCallback = (outline) => {
        return new Promise((resolve) => {
          // Auto mode: skip outline editing
          if (generationMode === 'auto') {
            resolve(outline);
            return;
          }
          
          // Structure-control and full-control modes: show editor
          setCurrentOutlineData({
            outline,
            resolver: resolve
          });
          setShowOutlineEditor(true);
        });
      };

      // Cost update callback
      const onCostUpdate: CostUpdateCallback = (stageName, stageCost, accumulatedCost) => {
        console.log(`💰 Cost update - ${stageName}: $${stageCost.totalCost.toFixed(4)} | Total: $${accumulatedCost.totalCost.toFixed(4)}`);
        setCurrentStageCost({ stage: stageName, cost: stageCost });
        setTotalCost(accumulatedCost);
      };

      const result = await generateArticleInteractive({
        competitors: competitorData,
        keywords: {
          additional: keywords.additional,
          missing: keywords.missing,
          present: keywords.present
        },
        projectContext: {
          description: projectDescription,
          project_prompt: projectPrompt, // Custom AI instructions from project settings
          tone: 'professional'
        },
        humanizeContent,
        language: selectedLanguage,
        contentType: selectedContentType,
        intent: selectedIntent,
        extractedData: extractedData || undefined,
        onProgress: (progressData) => {
          setProgress(progressData);
        },
        onPromptRequest,
        onOutlineRequest,
        onCostUpdate,
        onPartialResult: (html, stage, message) => {
          // Відразу показуємо згенерований контент
          console.log(`📄 Partial result (stage ${stage}):`, message);
          onComplete(html);
        }
      });

      toast.success('Article generated successfully!', { id: loadingToast });
      onComplete(result.finalArticle);
      setProgress(null);

    } catch (error) {
      console.error('Error generating article:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to generate article',
        { id: loadingToast }
      );
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <FileText className="h-5 w-5 text-indigo-600" />
          </div>
          <div className="flex items-center space-x-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                AI Article Generator
              </h3>
              <p className="text-sm text-gray-600">
                Multi-stage generation for high-quality, SEO-optimized articles
              </p>
            </div>
            <div className="relative">
              <button
                onMouseEnter={() => setShowInfo(true)}
                onMouseLeave={() => setShowInfo(false)}
                className="p-1 text-indigo-600 hover:text-indigo-800 transition-colors"
                aria-label="How it works"
              >
                <Info className="h-5 w-5" />
              </button>
              {showInfo && (
                <div className="absolute left-0 top-full mt-2 w-96 p-4 bg-blue-50 border border-blue-200 rounded-lg shadow-lg z-50">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    How it works:
                  </h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Analyzes competitors and creates structure (intro, sections, conclusion, CTA{generationMode !== 'auto' && ', FAQ'})</li>
                    <li>Generates each part sequentially - results in real-time!</li>
                    <li>Assembles everything into final article</li>
                  </ol>
                  <p className="text-xs text-blue-700 mt-3">
                    ⚡ Content appears in editor as each section is generated - no waiting!
                  </p>
                  {generationMode === 'auto' && (
                    <p className="text-xs text-blue-700 mt-2">
                      🚀 <strong>Automatic mode:</strong> Generation runs without stops
                    </p>
                  )}
                  {generationMode === 'structure-control' && (
                    <p className="text-xs text-blue-700 mt-2">
                      ⚙️ <strong>Structure control:</strong> Approve prompt and structure, rest is automatic
                    </p>
                  )}
                  {generationMode === 'full-control' && (
                    <p className="text-xs text-blue-700 mt-2">
                      🎯 <strong>Full control:</strong> Approve each stage separately
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isGenerating || isAnalyzing}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : isAnalyzing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Analyzing URL...
            </>
          ) : (
            <>
              <Zap className="h-4 w-4 mr-2" />
              Generate Article
            </>
          )}
        </button>
      </div>

      {/* Content Analysis Info */}
      {contentAnalysis && (
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
            <Info className="h-4 w-4 mr-2 text-green-600" />
            Content Characteristics (editable)
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Language Selector */}
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-gray-700 flex items-center">
                <Globe className="h-3 w-3 mr-1 text-blue-600" />
                Language
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="uk">🇺🇦 Ukrainian</option>
                <option value="ru">🇷🇺 Russian</option>
                <option value="en">🇬🇧 English</option>
              </select>
            </div>
            
            {/* Content Type Selector */}
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-gray-700 flex items-center">
                <Tag className="h-3 w-3 mr-1 text-purple-600" />
                Content Type
              </label>
              <select
                value={selectedContentType}
                onChange={(e) => setSelectedContentType(e.target.value)}
                className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="category">🛒 Category Page</option>
                <option value="product">📦 Product Page</option>
                <option value="blog-best">⭐ Best/Top List</option>
                <option value="blog-how-to">📚 How-to Guide</option>
                <option value="blog-guide">📖 Complete Guide</option>
                <option value="blog-review">✍️ Review</option>
                <option value="general">📄 General Article</option>
              </select>
            </div>
            
            {/* Search Intent Selector */}
            <div className="flex flex-col space-y-1">
              <label className="text-xs font-medium text-gray-700 flex items-center">
                <FileText className="h-3 w-3 mr-1 text-orange-600" />
                Search Intent
              </label>
              <select
                value={selectedIntent}
                onChange={(e) => setSelectedIntent(e.target.value)}
                className="text-sm px-2 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="commercial">💰 Commercial</option>
                <option value="informational">💡 Informational</option>
                <option value="navigational">🧭 Navigational</option>
                <option value="transactional">🛍️ Transactional</option>
              </select>
            </div>
          </div>
          
          {url && extractedData && (
            <div className="mt-3 pt-3 border-t border-green-200">
              <div className="flex items-start space-x-2 text-xs">
                <Link className="h-4 w-4 mt-0.5 text-green-600 flex-shrink-0" />
                <div className="flex-1">
                  <div className="font-medium mb-2">🔍 Real data extracted from URL:</div>
                  
                  {/* AI Analysis */}
                  {extractedData.aiAnalysis && (
                    <div className="mb-3 p-3 bg-blue-50 rounded border border-blue-200">
                      <div className="font-semibold text-blue-900 mb-2">🤖 Unique Information Extracted:</div>
                      <div className="text-gray-700 mb-1 text-sm">
                        <span className="font-medium">Page Type:</span> {extractedData.aiAnalysis.pageType}
                      </div>
                      <div className="text-gray-700 mb-2 text-sm">
                        <span className="font-medium">Unique Aspect:</span> {extractedData.aiAnalysis.mainTopic}
                      </div>
                      
                      {/* Унікальний підхід */}
                      {extractedData.aiAnalysis.uniqueApproach && extractedData.aiAnalysis.uniqueApproach.length > 0 && (
                        <div className="mb-2 p-2 bg-purple-50 border border-purple-200 rounded">
                          <span className="font-medium text-purple-900">🎯 Unique Approach:</span>
                          <ul className="list-disc list-inside mt-1 ml-2 text-purple-800 text-sm">
                            {extractedData.aiAnalysis.uniqueApproach.slice(0, 3).map((approach, i) => (
                              <li key={i}>{approach}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Сертифікати */}
                      {extractedData.aiAnalysis.certificates && extractedData.aiAnalysis.certificates.length > 0 && (
                        <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                          <span className="font-medium text-yellow-900">🏆 Certificates/Awards:</span>
                          <ul className="list-disc list-inside mt-1 ml-2 text-yellow-800 text-sm">
                            {extractedData.aiAnalysis.certificates.slice(0, 3).map((cert, i) => (
                              <li key={i}>{cert}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Price List з контекстом */}
                      {extractedData.aiAnalysis.priceList && extractedData.aiAnalysis.priceList.length > 0 && (
                        <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded">
                          <span className="font-medium text-green-900">💰 Exact Prices:</span>
                          <ul className="list-disc list-inside mt-1 ml-2 text-green-800 text-sm">
                            {extractedData.aiAnalysis.priceList.slice(0, 5).map((price, i) => (
                              <li key={i}>{price}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Конкурентні переваги */}
                      {extractedData.aiAnalysis.competitiveAdvantages && extractedData.aiAnalysis.competitiveAdvantages.length > 0 && (
                        <div className="mb-2 p-2 bg-indigo-50 border border-indigo-200 rounded">
                          <span className="font-medium text-indigo-900">🚀 Advantages:</span>
                          <ul className="list-disc list-inside mt-1 ml-2 text-indigo-800 text-sm">
                            {extractedData.aiAnalysis.competitiveAdvantages.slice(0, 3).map((adv, i) => (
                              <li key={i}>{adv}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Конкретні цифри */}
                      {extractedData.aiAnalysis.specificNumbers && extractedData.aiAnalysis.specificNumbers.length > 0 && (
                        <div className="mb-2 p-2 bg-orange-50 border border-orange-200 rounded">
                          <span className="font-medium text-orange-900">📊 Metrics:</span>
                          <ul className="list-disc list-inside mt-1 ml-2 text-orange-800 text-sm">
                            {extractedData.aiAnalysis.specificNumbers.slice(0, 4).map((num, i) => (
                              <li key={i}>{num}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {extractedData.aiAnalysis.importantFacts.length > 0 && (
                        <div className="text-gray-700 text-sm">
                          <span className="font-medium">Specific Facts:</span>
                          <ul className="list-disc list-inside mt-1 ml-2">
                            {extractedData.aiAnalysis.importantFacts.slice(0, 3).map((fact, i) => (
                              <li key={i}>{fact}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Structured Data */}
                  {extractedData.prices.length > 0 && (
                    <div className="text-gray-600">💵 Prices: {extractedData.prices.slice(0, 3).join(', ')}</div>
                  )}
                  {extractedData.brands.length > 0 && (
                    <div className="text-gray-600">🏷️ Brands: {extractedData.brands.slice(0, 3).join(', ')}</div>
                  )}
                  {extractedData.products.length > 0 && (
                    <div className="text-gray-600">📦 Products: {extractedData.products.length} found</div>
                  )}
                  {extractedData.benefits.length > 0 && (
                    <div className="text-gray-600">✨ Benefits: {extractedData.benefits.length} found</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Generation Mode Selector */}
      {!isGenerating && (
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex items-center space-x-2 mb-3">
            <Settings className="h-4 w-4 text-gray-500" />
            <h4 className="text-sm font-medium text-gray-700">Generation Mode</h4>
          </div>
          <div className="space-y-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="generationMode"
                value="auto"
                checked={generationMode === 'auto'}
                onChange={(e) => setGenerationMode(e.target.value as GenerationMode)}
                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">🚀 Automatic</div>
                <div className="text-xs text-gray-500">No stops - generates all stages at once</div>
              </div>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="generationMode"
                value="structure-control"
                checked={generationMode === 'structure-control'}
                onChange={(e) => setGenerationMode(e.target.value as GenerationMode)}
                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">⚙️ Structure Control</div>
                <div className="text-xs text-gray-500">Control first 2 steps (prompt + structure), then automatic</div>
              </div>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="radio"
                name="generationMode"
                value="full-control"
                checked={generationMode === 'full-control'}
                onChange={(e) => setGenerationMode(e.target.value as GenerationMode)}
                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">🎯 Full Control</div>
                <div className="text-xs text-gray-500">Approve each step separately</div>
              </div>
            </label>
          </div>
          
          {/* Humanizer Option */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={humanizeContent}
                onChange={(e) => setHumanizeContent(e.target.checked)}
                className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">✨ Humanize Content</div>
                <div className="text-xs text-gray-500">Make text more natural and human-like (same length)</div>
              </div>
            </label>
          </div>
        </div>
      )}

      {/* Admin: Cost toggle button */}
      {isAdmin && (progress || totalCost) && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowCost(!showCost)}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {showCost ? (
              <>
                <EyeOff className="h-4 w-4 mr-1.5" />
                Hide Cost
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-1.5" />
                Show Cost
              </>
            )}
          </button>
        </div>
      )}

      {/* Final Cost Summary (after generation) */}
      {isAdmin && showCost && !progress && totalCost && (
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1 flex items-center">
                <DollarSign className="h-4 w-4 mr-1 text-green-600" />
                Total Generation Cost
              </h4>
              <p className="text-xs text-gray-600">
                Model: {totalCost.model} | Tokens: {totalCost.inputTokens.toLocaleString()} in + {totalCost.outputTokens.toLocaleString()} out
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-700">
                ${totalCost.totalCost.toFixed(4)}
              </div>
              <div className="text-xs text-gray-500">
                Input: ${totalCost.inputCost.toFixed(4)} | Output: ${totalCost.outputCost.toFixed(4)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Display */}
      {progress && (
        <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="space-y-3">
            {/* Stage indicator */}
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-700">
                Stage {progress.stage} of {progress.totalStages}
              </span>
              <span className="text-gray-500">
                {Math.round(progress.progress)}%
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress.progress}%` }}
              />
            </div>

            {/* Current message */}
            <p className="text-sm text-gray-600">
              {progress.message}
            </p>

            {/* Cost display */}
            {isAdmin && showCost && totalCost && (
              <div className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200">
                <div className="flex items-center space-x-2 text-xs">
                  <DollarSign className="h-4 w-4 text-green-600" />
                  <span className="text-gray-700">
                    {currentStageCost && (
                      <span className="font-medium text-green-700">
                        {currentStageCost.stage}: ${currentStageCost.cost.totalCost.toFixed(4)}
                      </span>
                    )}
                    {currentStageCost && ' | '}
                    <span className="font-semibold text-gray-900">
                      Total: ${totalCost.totalCost.toFixed(4)}
                    </span>
                  </span>
                </div>
                <span className="text-xs text-gray-500">
                  {totalCost.inputTokens + totalCost.outputTokens} tokens
                </span>
              </div>
            )}

            {/* Section progress if available */}
            {progress.currentSection && progress.totalSections && (
              <div className="flex items-center space-x-2 text-xs text-gray-500">
                <div className="flex space-x-1">
                  {Array.from({ length: progress.totalSections }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-2 h-2 rounded-full ${
                        i < (progress.currentSection || 0)
                          ? 'bg-green-500'
                          : i === (progress.currentSection || 0) - 1
                          ? 'bg-indigo-500 animate-pulse'
                          : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span>
                  Section {progress.currentSection} of {progress.totalSections}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Prompt Editor Modal */}
      {showPromptEditor && currentPromptData && (
        <PromptEditor
          stageName={currentPromptData.stageName}
          stageNumber={currentPromptData.stageNumber}
          defaultPrompt={currentPromptData.prompt}
          onApprove={(editedPrompt) => {
            currentPromptData.resolver(editedPrompt);
            setShowPromptEditor(false);
            setCurrentPromptData(null);
          }}
          onCancel={() => {
            currentPromptData.resolver(''); // Cancel by returning empty
            setShowPromptEditor(false);
            setCurrentPromptData(null);
            setIsGenerating(false);
            toast.error('Article generation cancelled');
          }}
        />
      )}

      {/* Outline Editor Modal */}
      {showOutlineEditor && currentOutlineData && (
        <OutlineEditor
          outline={currentOutlineData.outline}
          onApprove={(editedOutline) => {
            currentOutlineData.resolver(editedOutline);
            setShowOutlineEditor(false);
            setCurrentOutlineData(null);
          }}
          onCancel={() => {
            // Return original outline to cancel
            currentOutlineData.resolver(currentOutlineData.outline);
            setShowOutlineEditor(false);
            setCurrentOutlineData(null);
            setIsGenerating(false);
            toast.error('Article generation cancelled');
          }}
        />
      )}
    </div>
  );
}
